import assertEventType from 'common/bento/lib/assertEventType';
import { MachineGuards } from 'common/bento/types/MachineConfig';
// eslint-disable-next-line no-restricted-imports -- Feature flags to be migrated
import features from 'featureFlags';

import { Role } from '../../../__generated__/GQL';
import shouldUserGoThroughInHouseSasuCreation from '../../Bento/flows/signup/libs/shouldUserGoThroughInHouseSasuCreation';
import getCompanyApplicationType from '../lib/getCompanyApplicationType';
import getIsBeneficiary from '../lib/getIsBeneficiary';
import { ApplicationType } from '../types';
import { Model } from './model';

export enum Guard {
  IsCapitalDeposit = 'isCapitalDeposit',
  IsShineStart = 'isShineStart',
  IsSasuCreation = 'isSasuCreation',
  IsStandard = 'isStandard',
  IsMandatee = 'isMandatee',
  IsFirstOnboarding = 'isFirstOnboarding',
  IsInvitee = 'isInvitee',
  IsInvitedAccountHolderCapitalDeposit = 'isInvitedAccountHolderCapitalDeposit',
  ShouldDoInHouseSasuCreation = 'shouldDoInHouseSasuCreation',
  ShouldSelectApplication = 'shouldSelectApplication',
}

const guards: MachineGuards<Model, Guard> = {
  isCapitalDeposit: ({ getModuleData }) => {
    const { applicationTypeContext, companyData } = getModuleData();

    if (!companyData) {
      return false;
    }

    return (
      (getCompanyApplicationType({ company: companyData.company }) ??
        applicationTypeContext.applicationType) ===
      ApplicationType.CapitalDeposit
    );
  },

  isFirstOnboarding: ({ getModuleData }) => {
    const { user } = getModuleData();

    // If user has no companies linked to them, it means it’s their first onboarding
    return user.companyUsers.length === 0;
  },

  isInvitedAccountHolderCapitalDeposit: ({ getModuleData }) => {
    const { companyData } = getModuleData();

    // No company data means the company does not exist,
    // so the user can’t be an invitee.
    if (!companyData) {
      return false;
    }

    return (
      Boolean(companyData.companyUser.invitation) &&
      companyData.company.isCreatedByMandatee &&
      companyData.companyUser.roles.includes(Role.AccountHolder)
    );
  },

  isInvitee: ({ getModuleData }) => {
    return getIsBeneficiary(getModuleData().companyData?.companyUser);
  },

  isMandatee: ({ getModuleData }) => {
    const { user } = getModuleData();

    if (user.metadata.isMandatee === false) {
      return false;
    }

    if (!features.enableMandateeOnboarding) {
      throw new Error(
        'A mandatee accessed the onboarding while the feature is not enabled',
      );
    }

    // TODO(LED-1716): once CompaniesMetadata.mandateeUid is created, if the company entity hasn't been created by the mandatee,
    // throw an error, because we won't support own companies or beneficiary flow for mandatees at first
    // ie: if(companyData.metadata.mandateeUid !== user.uid) throw Error()
    return true;
  },

  isSasuCreation: ({ getModuleData }) => {
    const { applicationTypeContext, companyData } = getModuleData();

    if (!companyData) {
      return applicationTypeContext.applicationType === 'sasu-creation';
    }

    return (
      (getCompanyApplicationType({ company: companyData.company }) ??
        applicationTypeContext.applicationType) === ApplicationType.SasuCreation
    );
  },

  isShineStart: ({ getModuleData }) => {
    const { applicationTypeContext, companyData } = getModuleData();

    if (!companyData) {
      return false;
    }

    return (
      (getCompanyApplicationType({ company: companyData.company }) ??
        applicationTypeContext.applicationType) === ApplicationType.ShineStart
    );
  },

  isStandard: ({ getModuleData }) => {
    const { applicationTypeContext, companyData } = getModuleData();

    if (!companyData) {
      return false;
    }

    return (
      (getCompanyApplicationType({ company: companyData.company }) ??
        applicationTypeContext.applicationType) === ApplicationType.Standard
    );
  },

  shouldDoInHouseSasuCreation: (context, event) => {
    assertEventType(event, 'SUBMIT_COMPANY_CREATION_LEGAL_FORM');

    return shouldUserGoThroughInHouseSasuCreation(event.data);
  },

  shouldSelectApplication: ({ getModuleData }) => {
    const { companyContext, user } = getModuleData();

    return user.companyUsers.length > 0 && !companyContext.companyProfileId;
  },
};

export default guards;
